import {
    faCircleExclamation,
    faCircleMinus,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Typography from "@vapor/react-extended/ExtendedTypography";
import { NotificationInline } from "@vapor/react-material";
import type { ReactElement } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type {
    ExpiringDelegated,
    ExpiringIntermediary,
    ExpiringSecurityEnvironment,
} from "../../../../../core/api/use-get-expiring-identities/ExpiringIdentitiesDTO";
import type { Variant } from "./WarningNotificationContent";
import { WarningNotificationContent } from "./WarningNotificationContent";

const PRESENTATION: Record<
    Variant,
    {
        severity: "error" | "warning";
        icon: typeof faCircleMinus;
        titleKey: string;
    }
> = {
    expiredCredentials: {
        severity: "error",
        icon: faCircleMinus,
        titleKey:
            "views.home.components.tax_submissions_table_header.expiringIdentities.credentials.expired",
    },
    expiredSecurityEnvironments: {
        severity: "error",
        icon: faCircleMinus,
        titleKey:
            "views.home.components.tax_submissions_table_header.expiringIdentities.securityEnvironment.expired",
    },
    expiringCredentials: {
        severity: "warning",
        icon: faCircleExclamation,
        titleKey:
            "views.home.components.tax_submissions_table_header.expiringIdentities.credentials.expiring",
    },
    expiringSecurityEnvironments: {
        severity: "warning",
        icon: faCircleExclamation,
        titleKey:
            "views.home.components.tax_submissions_table_header.expiringIdentities.securityEnvironment.expiring",
    },
};

interface ExpiringIdentityNotificationProps {
    variant: Variant;
    identities:
        | ExpiringIntermediary[]
        | ExpiringDelegated[]
        | ExpiringSecurityEnvironment[];
}

export const ExpiringIdentityNotification = ({
    variant,
    identities,
}: ExpiringIdentityNotificationProps): ReactElement | null => {
    const { t } = useTranslation();
    const [dismissed, setDismissed] = useState(false);

    if (dismissed || identities.length === 0) return null;

    const presentation = PRESENTATION[variant];

    return (
        <NotificationInline
            icon={<FontAwesomeIcon icon={presentation.icon} />}
            severity={presentation.severity}
            variant="outlined"
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
            }}
        >
            <WarningNotificationContent
                variant={variant}
                identities={identities}
                onDismiss={() => setDismissed(true)}
            >
                <Typography variant="body">
                    {t(presentation.titleKey)}
                </Typography>
            </WarningNotificationContent>
        </NotificationInline>
    );
};
